<template>
    <div>
        <v-dialog v-model="showDialog" max-width="600px">
            <v-card>
                <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
                    <div class="font-weight-bold fill-width">
                        <span>{{ title }}</span>
                        <v-btn icon @click="closeDialog" class="float-right"><v-icon
                            class="primary primarytext--text">mdi-close</v-icon></v-btn>
                    </div>
                </v-card-title>
                <div v-if="oneMatch">
                    <div class="ma-4">
                        {{ $translate('The competition can be concluded once all results have been reported. Once the competition is concluded, no additional results can be reported.') }}
                    </div>
                    <div v-if="teamsNeedingResult.length > 0">
                        <v-alert v-if="isMultiAttempt" dense outlined type="warning" class="mx-4 mb-0">
                            {{ $translate('Not all results have been reported, report the last one before marking as finished.') }}
                        </v-alert>
                        <div class="ma-4">
                            <div class="font-weight-bold">{{ missingResultsTitle }}</div>
                            <v-list>
                                <v-list-item v-for="team in teamsNeedingResult" :key="team.id"
                                             class="d-flex justify-space-between align-center">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ team.name }}</v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-btn small rounded fab color="primary" @click="addResult(team)">
                                            <v-icon>mdi-plus</v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </div>
                    </div>
                    <v-card-actions class="ma-2">
                        <v-btn width="50%" class="zoezi-button-thin primarytext--text" color="secondary" @click="closeDialog">{{ $translate("Cancel") }}</v-btn>
                        <v-btn width="50%" class="zoezi-button-thin primarytext--text" :disabled="teamsNeedingResult.length > 0" color="primary" @click="setResult" :loading="saving">{{ $translate("Finish") }}</v-btn>
                    </v-card-actions>
                </div>
                <div v-else>
                    <v-form v-model="formValid">
                        <div class="ma-4">
                            <div v-for="(field, index) in teamFields" :key="index">
                                <v-card class="ma-2">
                                    <v-card-title>
                                        <div class="font-weight-bold fill-width">
                                            <span>{{ field.title }}</span>
                                        </div>
                                    </v-card-title>
                                    <v-card-text>
                                        <form-field v-if="field.resultTypeField" v-model="field.resultTypeField.value" :field="field.resultTypeField"
                                                    star dense></form-field>
                                        <template v-if="isMultiAttempt && field.resultTypeField && field.resultTypeField.value == 0">
                                            <form-field v-for="(attempt, ai) in field.attemptFields" :key="ai"
                                                        v-model="attempt.value" :field="attempt" dense></form-field>
                                            <v-alert dense outlined type="info">{{ $translate('Leave an attempt blank if not taken, or enter X for a foul.') }}</v-alert>
                                        </template>
                                        <template v-else-if="isSetResult">
                                            <template v-if="field.resultTypeField.value == 0">
                                                <form-field v-model="field.resultField.value" :field="field.resultField" star dense></form-field>
                                                <v-alert dense outlined type="info">{{ $translate('Example') }}: {{ match.result_type == 'padel' ? '6-2, 4-6, 1-6' : '21-18, 18-21, 21-19' }}</v-alert>
                                            </template>
                                            <form-field v-else v-model="field.winnerField.value" :field="field.winnerField" star dense></form-field>
                                        </template>
                                        <form-field
                                            v-else-if="field.resultTypeField.value == 0"
                                            v-model="field.resultField.value"
                                            :field="field.resultField" star
                                            dense
                                        >
                                        </form-field>
                                    </v-card-text>
                                </v-card>
                            </div>
                            <form-field v-model="finishMatchField.value" :field="finishMatchField"></form-field>
                        </div>
                    </v-form>
                    <v-card-actions>
                        <v-btn width="100%" class="zoezi-button-thin mb-2 primarytext--text" :disabled="!formValid" color="primary" @click="setResult" :loading="saving">{{ $translate("Save") }}</v-btn>
                    </v-card-actions>
                </div>
            </v-card>
        </v-dialog>
        <teamsport-event-dialog :match="match" :teamId="selectedTeam.id" v-model="showEventDialog" oneMatch v-if="showEventDialog" />
    </div>
</template>

<script>
import TeamsportEventDialog from './TeamsportEventDialog.vue';
import FormField from '../components/FormField.vue';
import { resultRules } from '../util';

export default {
    name: 'zoezi-teamsport-set-result-dialog',

    components: {
        TeamsportEventDialog,
        FormField,
    },

    props: {
        value: {
            type: Boolean,
            default: false,
        },
        match: {
            type: Object,
            required: true,
        },
        oneMatch: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            loadingInvalidResults: false,
            invalidResults: [],
            missingFields: [],
            teamFields: [],
            formValid: false,
            saving: false,
            events: null, // null = not loaded yet (warn on clear until we know)
            eventTypes: [],
            selectedTeam: null,
            unsubscribe: [],
            showEventDialog: false,
            finishMatchField: {
                name: 'finishMatch',
                title: 'Match is finished',
                type: 'checkbox',
                value: true,
            }
        };
    },

    computed: {
        showDialog: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        numAttempts() {
            return this.match.number_of_attempts || 0;
        },
        teamsNeedingResult() {
            // For multi-attempt events every attempt slot must be filled (a value or a foul
            // marker such as 'X') before the match can be finished — an empty slot means the
            // result has not been fully reported yet. For single-result events a competitor
            // just needs some result (or an invalid/walkover marker).
            return this.match.teams.filter(team => {
                const result = this.match.result?.find(r => r?.team_id == team.id);
                if (result?.invalid_id) {
                    return false; // invalid result (e.g. DNS / walkover) is a complete, deliberate state
                }
                if (this.isMultiAttempt) {
                    const attempts = result?.attempts || [];
                    for (let i = 0; i < this.numAttempts; i++) {
                        const a = attempts[i];
                        if (a == null || String(a).trim() === '') {
                            return true;
                        }
                    }
                    return false;
                }
                return !result?.result;
            });
        },
        missingResultsTitle() {
            const single = this.teamsNeedingResult.length === 1;
            if (this.isMultiAttempt) {
                return single
                    ? this.$translate('Attempts are missing for the following participant:')
                    : this.$translate('Attempts are missing for the following participants:');
            }
            return single
                ? this.$translate('Result is missing for the following participant:')
                : this.$translate('Results are missing for the following participants:');
        },
        title() {
            return this.oneMatch ? this.$translate('Mark as finished') : this.$translate('Set result');
        },
        isMultiAttempt() {
            return this.match.number_of_attempts > 1;
        },
        isSetResult() {
            return this.match.result_type == 'padel' || this.match.result_type == 'sets';
        },
        isCup() {
            // Cup results can't be cleared (bracket advancement has no undo); league ones can.
            return this.match && this.match.is_cup === true;
        },
        clearing() {
            // Unchecking "match is finished" on a league match clears the result (back to unplayed).
            return !this.isCup && !this.finishMatchField.value;
        }
    },

    watch: {
        'finishMatchField.value'() {
            this.applyClearingState();
        },
        isCup() {
            this.applyClearingState();
        }
    },

    methods: {
        possiblyShowError(response) {
            if (!response.ok) {
                response.json().then(data => {
                    this.$store.commit('showErrorDialog', { title: this.$translate('Error'), text: `${this.$translate('Something went wrong')}. ${this.$translate(data?.message)}`});
                });
            }
        },
        attemptRules() {
            // Like resultRules, but allow a blank attempt, a foul marker ("X"), and a Swedish decimal comma
            const baseRules = resultRules(this.match?.result_type);
            return [value => {
                if (value === '' || value == null) return true;
                const v = String(value).trim();
                if (/^[xX]$/.test(v)) return true;
                return baseRules[0](v.replace(',', '.'));
            }];
        },
        initializeTeamFields() {
            if (!this.match || !this.match.teams) return;

            if (this.isSetResult) {
                const result = this.match.result ? this.match.result[0] : null;
                this.teamFields = [{
                    title: this.match.teams[0].name + ' - ' + this.match.teams[1].name,
                    resultTypeField: {
                        name: 'setResultType',
                        title: 'Result type',
                        type: 'select',
                        required: true,
                        data: this.invalidResults.map(ir => ({ value: ir.id, text: ir.name })),
                        value: result?.invalid_id || 0,
                    },
                    resultField: {
                        title: 'Result',
                        type: 'text',
                        required: true,
                        value: result?.invalid_id ? '' : result?.result,
                        rules: resultRules(this.match?.result_type),
                    },
                    winnerField: {
                        name: 'setWinner',
                        title: 'Winner',
                        type: 'select',
                        required: true,
                        data: this.match.teams.map(t => ({ value: t.id, text: t.name })),
                        value: result?.invalid_id ? result?.team_id : null,
                    },
                }];
            } else {
                this.teamFields = this.match.teams.map(team => {
                    const result = this.match.result?.find(r => r?.team_id === team.id);
                    return {
                        title: team.name,
                        id: team.id,
                        resultTypeField: {
                            name: 'resultType_' + team.id,
                            title: 'Result type',
                            type: 'select',
                            required: true,
                            data: this.invalidResults.map(ir => ({
                                value: ir.id,
                                text: ir.name,
                            })),
                            value: result?.invalid_id || 0,
                        },
                        resultField: {
                            name: 'result_' + team.id,
                            title: 'Result',
                            type: 'text',
                            required: true,
                            value: result?.result,
                            rules: resultRules(this.match?.result_type),

                        },
                        attemptFields: this.isMultiAttempt
                            ? Array.from({ length: this.match.number_of_attempts }, (_, i) => ({
                                name: 'attempt_' + team.id + '_' + i,
                                title: this.$translate('Attempt') + ' ' + (i + 1),
                                type: 'text',
                                value: result?.attempts?.[i] ?? '',
                                rules: this.attemptRules(),
                                suffix: this.match.unit || undefined,
                            }))
                            : null,
                    };
                });
            }
            this.applyClearingState();
        },
        applyClearingState() {
            // While clearing a league result, no result is required — empty is allowed.
            const required = !this.clearing;
            this.teamFields.forEach(field => {
                if (field.resultField) field.resultField.required = required;
                if (field.winnerField) field.winnerField.required = required;
            });
        },
        getInvaldResults() {
            this.loadingInvalidResults = true;
            this.$api.get('/api/teamsport/resultinvalid/get').then(response => {
                response.unshift({ id: 0, name: this.$translate('Valid result') });
                this.invalidResults = response;
                this.initializeTeamFields();
                this.loadingInvalidResults = false;
            });
        },
        fetchEvents() {
            this.$api.get('/api/teamsport/event/get', { match_id: this.match.id })
                .then(events => { this.events = events || []; })
                .catch(() => { this.events = null; });
        },
        closeDialog() {
            this.selectedTeam = null;
            this.showEventDialog = false;
            this.showDialog = false;
        },
        setResult() {
            // Warn before clearing a result that still has reported events (goals etc.).
            // events == null means the lookup hasn't returned, so warn rather than risk
            // silently removing events on a slow/failed fetch.
            if (this.clearing && (this.events == null || this.events.length)) {
                this.$store.commit('showConfirmDialog', {
                    title: this.$translate('Clear result'),
                    text: this.$translate('This match has reported events (goals etc.). Clearing the result will also remove them. Do you want to continue?'),
                    actionText: this.$translate('Continue'),
                    action: () => this.doSetResult(),
                });
                return;
            }
            this.doSetResult();
        },
        doSetResult() {
            this.saving = true;
            const clearing = this.clearing;
            let data = null;
            if (this.isSetResult) {
                if (clearing) {
                    data = { match_id: this.match.id, result: null, invalid_id: null, winner_team_id: null, finished: false };
                } else {
                    const field = this.teamFields[0];
                    const invalid_id = field.resultTypeField.value == 0 ? null : field.resultTypeField.value;
                    data = {
                        match_id: this.match.id,
                        result: invalid_id ? null : field.resultField.value,
                        invalid_id,
                        winner_team_id: invalid_id ? field.winnerField.value : null,
                        finished: this.finishMatchField.value,
                    };
                }
            } else if (this.oneMatch) {
                data = {
                    match_id: this.match.id,
                    result: this.match.result,
                    finished: true,
                };
            } else {
                data = {
                    match_id: this.match.id,
                    result: this.teamFields.map(field => {
                        // Unfinishing a league match clears the result, ignoring leftover scores.
                        if (clearing) {
                            return this.isMultiAttempt
                                ? { team_id: field.id, invalid_id: null, attempts: [] }
                                : { team_id: field.id, invalid_id: null, result: null };
                        }
                        const invalid_id = field.resultTypeField.value == 0 ? null : field.resultTypeField.value;
                        if (this.isMultiAttempt) {
                            return {
                                team_id: field.id,
                                invalid_id,
                                attempts: invalid_id ? [] : field.attemptFields.map(a => a.value == null ? '' : String(a.value)),
                            };
                        }
                        return {
                            team_id: field.id,
                            invalid_id,
                            result: field.resultField.value || null,
                        };
                    }),
                    finished: this.finishMatchField.value,
                };
            }
            this.$api.post('/api/teamsport/match/setresult', data).then((response) => {
                this.saving = false;
                this.closeDialog();
                this.possiblyShowError(response)
            });
        },
        addResult(item) {
            this.selectedTeam = item;
            this.showEventDialog = true;
        }
    },

    mounted() {
        this.getInvaldResults();
        this.fetchEvents();
        this.unsubscribe.push(this.$api.subscribe({
            type: 'teamsportresultinvalid',
            callback: () => this.getInvaldResults()
        }));
        
        this.unsubscribe.push(this.$api.subscribe({
            type: 'teamsportevent',
            callback: (_, event) => {
                if (event.data.find(x => x.match_id == this.match.id)) {
                    this.fetchEvents();
                }
            }
        }));
    },

    destroyed() {
        this.unsubscribe.forEach(unsubscribe => unsubscribe());
    },
}

</script>
